import React from 'react';
import { Box, Typography, Grid } from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { ProductCategory, ProductType } from '../App';
import StepHeading from './StepHeading';
import SelectableCard from './SelectableCard';
import WizardFooter from './WizardFooter';

interface ProductSelectionProps {
  categories?: ProductCategory[];
  onCategorySelect?: (categories: ProductCategory[]) => void;
  onNext?: (selectedProducts: ProductType[]) => void;
  onBack?: () => void;
  onSelectionChange?: (selection: string[]) => void;
  step: number;
}

interface ProductOption {
  id: ProductType;
  title: string;
  description: string;
  image: string;
}

declare global {
  interface Window {
    ISK_PLUGIN_URL?: string;
  }
}

// In WordPress liefert das Plugin seine URL als window.ISK_PLUGIN_URL (Bilder unter build/static/images).
// Fallback 1: Plugin-URL aus dem src des geladenen Bundle-Scripts ableiten (funktioniert auch ohne PHP-Update).
// Fallback 2: Ohne WordPress (Dev-Server, lokale Preview) liegen die Bilder aus dem Repo unter /images (public/images).
const getImageBase = (): string => {
  if (window.ISK_PLUGIN_URL) {
    return `${window.ISK_PLUGIN_URL.replace(/\/$/, '')}/build/static/images`;
  }
  const pluginScript = Array.from(document.scripts).find(script =>
    script.src.includes('/insektenschutz-konfigurator/build/')
  );
  if (pluginScript) {
    return `${pluginScript.src.split('/build/')[0]}/build/static/images`;
  }
  return '/images';
};

const IMAGE_BASE = getImageBase();

const categoryOptions: { id: ProductCategory; title: string; description: string; image: string }[] = [
  { id: 'fenster', title: 'Fenster', description: 'Insektenschutz für Ihre Fenster', image: `${IMAGE_BASE}/Fensterrollo.jpg.webp` },
  { id: 'tueren', title: 'Türen', description: 'Insektenschutz für Ihre Türen', image: `${IMAGE_BASE}/Drehtuere.jpg.webp` },
  { id: 'lichtschacht', title: 'Lichtschacht', description: 'Schutz für Ihre Lichtschächte', image: `${IMAGE_BASE}/Lichtschacht.jpg.webp` },
];

const products: Record<ProductCategory, ProductOption[]> = {
  fenster: [
    { id: 'spannrahmen_fenster', title: 'Spannrahmen', description: 'Fester Schutz zum Einspannen', image: `${IMAGE_BASE}/Spannrahmen.jpg.webp` },
    { id: 'drehfenster', title: 'Drehfenster', description: 'Klassischer Schutz zum Öffnen', image: `${IMAGE_BASE}/Drehfenster.jpg.webp` },
    { id: 'pendelfenster', title: 'Pendelfenster', description: 'Praktischer Schutz zum Durchschieben', image: `${IMAGE_BASE}/Drehfenster.jpg.webp` },
    { id: 'rollo_fenster', title: 'Fenster Rollo', description: 'Flexibler Schutz zum Auf- und Abrollen', image: `${IMAGE_BASE}/Fensterrollo.jpg.webp` },
    { id: 'rollo_dachfenster', title: 'Dachfenster Rollo', description: 'Rollo-Lösung für Dachfenster', image: `${IMAGE_BASE}/Dachfensterrollo.jpg.webp` },
  ],
  tueren: [
    { id: 'drehtuer', title: 'Drehtür', description: 'Klassische Schutztür zum Öffnen', image: `${IMAGE_BASE}/Drehtuere.jpg.webp` },
    { id: 'pendeltuer', title: 'Pendeltür', description: 'Praktische Tür zum Durchgehen', image: `${IMAGE_BASE}/Pendeltuere.jpg.webp` },
    { id: 'plisseetuer', title: 'Plisseetür', description: 'Platzsparende Falttür', image: `${IMAGE_BASE}/Plisseetuere.webp` },
    { id: 'rollo_tuer', title: 'Tür-Rollo', description: 'Flexibler Schutz zum Auf- und Abrollen', image: `${IMAGE_BASE}/RolloTuere.jpg.webp` },
    { id: 'schiebetuer', title: 'Schiebetür', description: 'Elegante Lösung zum Verschieben', image: `${IMAGE_BASE}/Schiebeanlage.jpg.webp` },
  ],
  lichtschacht: [
    { id: 'lichtschacht', title: 'Lichtschacht', description: 'Schutzgitter für Ihren Lichtschacht', image: `${IMAGE_BASE}/Lichtschacht.jpg.webp` },
  ],
};

const SectionHeading: React.FC<{ title: string }> = ({ title }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, mt: 1 }}>
    <Typography sx={{ fontWeight: 700, color: 'text.primary' }}>{title}</Typography>
    <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
  </Box>
);

const MultiSelectBanner: React.FC<{ text: string }> = ({ text }) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 1,
      bgcolor: '#E8F1FB',
      borderRadius: '10px',
      px: 2,
      py: 1.25,
      mb: 3.5,
    }}
  >
    <AddCircleIcon sx={{ color: 'primary.main', fontSize: 20 }} />
    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
      {text}
    </Typography>
  </Box>
);

const ProductSelection: React.FC<ProductSelectionProps> = ({
  categories,
  onCategorySelect,
  onNext,
  onBack,
  onSelectionChange,
  step,
}) => {
  const [selectedProducts, setSelectedProducts] = React.useState<ProductType[]>([]);
  const [selectedCategories, setSelectedCategories] = React.useState<ProductCategory[]>(categories || []);

  const handleCategoryToggle = (category: ProductCategory) => {
    const next = selectedCategories.includes(category)
      ? selectedCategories.filter(c => c !== category)
      : [...selectedCategories, category];
    setSelectedCategories(next);
    onSelectionChange?.(next);
  };

  const handleProductToggle = (productId: ProductType) => {
    const next = selectedProducts.includes(productId)
      ? selectedProducts.filter(id => id !== productId)
      : [...selectedProducts, productId];
    setSelectedProducts(next);
    onSelectionChange?.(next);
  };

  const handleNext = () => {
    if (onNext && selectedProducts.length > 0) {
      onNext(selectedProducts);
    }
  };

  const handleCategoryNext = () => {
    if (onCategorySelect && selectedCategories.length > 0) {
      onCategorySelect(selectedCategories);
    }
  };

  const renderProductGrid = (items: ProductOption[]) => (
    <Grid container spacing={2.5} sx={{ mb: 3 }}>
      {items.map(product => (
        <Grid item xs={12} sm={6} md={4} key={product.id}>
          <SelectableCard
            title={product.title}
            description={product.description}
            image={product.image}
            selected={selectedProducts.includes(product.id)}
            onToggle={() => handleProductToggle(product.id)}
          />
        </Grid>
      ))}
    </Grid>
  );

  return (
    <Box sx={{ width: '100%' }} className="product-selection-container">
      {/* Schritt 1: Bereiche */}
      {step === 0 && (
        <Box className="category-selection">
          <StepHeading
            step={1}
            title="Was möchten Sie schützen?"
            subtitle="Wählen Sie einen oder mehrere Bereiche — Sie können später alles in einer Anfrage zusammenfassen."
          />
          <MultiSelectBanner text="Mehrfachauswahl möglich — tippen Sie einfach mehrere an" />

          <Grid container spacing={2.5}>
            {categoryOptions.map(cat => (
              <Grid item xs={12} sm={4} key={cat.id}>
                <SelectableCard
                  title={cat.title}
                  description={cat.description}
                  image={cat.image}
                  selected={selectedCategories.includes(cat.id)}
                  onToggle={() => handleCategoryToggle(cat.id)}
                />
              </Grid>
            ))}
          </Grid>

          <WizardFooter
            nextLabel="Weiter →"
            nextDisabled={selectedCategories.length === 0}
            onNext={handleCategoryNext}
          />
        </Box>
      )}

      {/* Schritt 2: Produkte */}
      {step === 1 && categories && (
        <Box className="product-selection">
          <StepHeading
            step={2}
            title="Welche Produkte brauchen Sie?"
            subtitle="Wählen Sie beliebig viele Varianten — die Anzahl je Produkt legen Sie im nächsten Schritt fest."
          />

          {categories.map(category => {
            if (category === 'fenster') {
              const windowProducts = products.fenster.filter(p => p.id !== 'rollo_dachfenster');
              const roofWindowProducts = products.fenster.filter(p => p.id === 'rollo_dachfenster');
              return (
                <Box key={category}>
                  <SectionHeading title="Fenster" />
                  {renderProductGrid(windowProducts)}
                  <SectionHeading title="Dachfenster" />
                  {renderProductGrid(roofWindowProducts)}
                </Box>
              );
            }
            return (
              <Box key={category}>
                <SectionHeading title={category === 'tueren' ? 'Türen' : 'Lichtschacht'} />
                {renderProductGrid(products[category])}
              </Box>
            );
          })}

          <WizardFooter
            onBack={onBack}
            nextLabel="Weiter →"
            nextDisabled={selectedProducts.length === 0}
            onNext={handleNext}
          />
        </Box>
      )}
    </Box>
  );
};

export default ProductSelection;
